import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { TZDate } from '@date-fns/tz';
import { createCollection, liveQueryCollectionOptions } from '@tanstack/db';
import { electricCollectionOptions } from '@tanstack/electric-db-collection';
import { ElectricConfigService } from 'app/services/electric-config.service';
import { FLAGS } from 'app/services/feature-flag.service';
import { HttpService } from 'app/services/http-service';
import { addDays, startOfDay } from 'date-fns';

// How often to check whether the school day has rolled over. A single timer set for
// midnight can't be trusted on its own: while a device sleeps its timers are paused or
// pushed back, so it wakes after midnight still on yesterday's shape.
const DAY_ROLLOVER_CHECK_INTERVAL_MS = 60_000;

export type HallPass = {
	id: number;
	student_id: number;
	issuer_id: number;
	origin_id: number;
	destination_id: number;
	start_time: string;
	end_time: string | null;
	expiration_time: string | null;
	cancelled: string | null;
	removed: boolean;
	travel_type: string;
	was_scheduled: boolean;
	needs_check_in: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class ElectricSQLService {
	private electricConfig = inject(ElectricConfigService);
	private httpService = inject(HttpService);
	private destroyRef = inject(DestroyRef);

	// Increments when the school day rolls over, causing hall pass collections to recreate
	// and discard the previous day's in-memory data.
	private readonly dayTick = signal(0);

	// The anchor the current hall pass collection was built with, so a rollover check can
	// tell whether the school day has moved on without it.
	private builtAnchor: string | null = null;

	private readonly schoolId = computed(() => this.httpService.currentSchool()?.id);

	// Syncs hall passes that nothing reads, so we can watch Electric under real load
	// before any UI depends on it.
	private readonly shadowHallPasses = computed(
		() => this.httpService.currentSchool()?.feature_flags_v2?.includes(FLAGS.ShadowElectricHallPasses) ?? false
	);

	constructor() {
		// The collection is only rebuilt when the day actually changed, so checking often
		// costs nothing. The periodic check catches a midnight that passed while the device
		// slept or the tab was throttled, and visibilitychange catches the wake-up itself.
		const intervalId = setInterval(() => this.refreshIfDayRolledOver(), DAY_ROLLOVER_CHECK_INTERVAL_MS);
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') this.refreshIfDayRolledOver();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		this.destroyRef.onDestroy(() => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		});

		effect((onCleanup) => {
			if (!this.shadowHallPasses()) return;
			const collection = this.hallPassesCollection();
			void collection.preload();
			// Covers logout, school switches and new school days. Users mostly leave by
			// closing the tab, which never gets here, so Electric has to age out
			// abandoned shapes on its own.
			onCleanup(() => void collection.cleanup());
		});
	}

	// The school day containing `now`, as [anchor, anchorEnd). The end is exclusive and
	// computed in the school's timezone, so DST days are 23 or 25 hours. Both bounds come
	// from the same instant so they can't straddle midnight.
	private static dayWindowInTz(tz: string, now = new Date()): { anchor: string; anchorEnd: string } {
		const dayStart = startOfDay(new TZDate(now, tz));
		return { anchor: dayStart.toISOString(), anchorEnd: startOfDay(addDays(dayStart, 1)).toISOString() };
	}

	private refreshIfDayRolledOver(): void {
		if (!this.shadowHallPasses()) return;
		const tz = this.httpService.schoolTimezone() || 'UTC';
		if (ElectricSQLService.dayWindowInTz(tz).anchor !== this.builtAnchor) {
			this.dayTick.update((n) => n + 1);
		}
	}

	// Hall passes

	// Recreated on the dayTick signal when the school day rolls over. Pushing the
	// anchor and its end into the shape bounds it to one day of data, so Electric
	// can LRU-evict the previous day's shape once it's no longer requested, and
	// new passes stop matching (and refreshing) old days' shapes. Passes
	// scheduled for a later day only appear once that day's shape is requested.
	private hallPassesCollection = computed(() => {
		this.dayTick();
		// The Worker scopes the shape to the school sent with each request, so a
		// school switch needs a new collection.
		const schoolId = this.schoolId();
		const tz = this.httpService.schoolTimezone() || 'UTC';
		const { anchor, anchorEnd } = ElectricSQLService.dayWindowInTz(tz);
		this.builtAnchor = anchor;
		return createCollection(
			electricCollectionOptions<HallPass>({
				id: `hallPasses_${schoolId}_${anchor}`,
				// The Worker fixes this shape's columns and filter, and scopes it to
				// the student for students; the anchor window is its only parameter.
				shapeOptions: { ...this.electricConfig.shapeOptions('hall_passes'), params: { anchor, anchor_end: anchorEnd } },
				getKey: (item) => item.id,
			})
		);
	});

	// Currently active passes: started but not yet ended.
	// Uses fn.where so that new Date() is evaluated fresh on each re-run triggered by
	// incoming Electric updates, rather than being captured once at collection definition time.
	activeHallPassesCollection = computed(() =>
		createCollection(
			liveQueryCollectionOptions({
				query: (q) =>
					q.from({ pass: this.hallPassesCollection() }).fn.where((row) => {
						const now = new Date().toISOString();
						return row.pass.start_time <= now && (row.pass.end_time == null || row.pass.end_time >= now);
					}),
			})
		)
	);

	// Scheduled passes that have not started yet.
	futureHallPassesCollection = computed(() =>
		createCollection(
			liveQueryCollectionOptions({
				query: (q) => q.from({ pass: this.hallPassesCollection() }).fn.where((row) => row.pass.start_time > new Date().toISOString()),
			})
		)
	);
}
